import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, PackageCheck, ShieldCheck, Clock, Scale, KeyRound } from "lucide-react";
import { api } from "@/lib/api";
import { gbp } from "@/lib/pricing";
import { useCategories } from "@/context/SiteContentContext";
import { usePartner } from "@/context/PartnerContext";
import { useCopy } from "@/lib/copy";

const MAX_ONLINE_ORDER_WEIGHT_G = 20000;

export default function ShippingInfo() {
  const cats = useCategories();
  const { brand, isPartner, partner } = usePartner();
  const { t, C } = useCopy();
  const fingerprint = isPartner ? partner?.pgp_fingerprint : "8AF3 87B4 FCEB 5A87 132D\u00a0 02F5 5237 B054 7123 B7EA";
  const [zones, setZones] = useState([]);
  useEffect(() => { api.get("/shipping/zones").then((r) => setZones(r.data)); }, []);
  const sortedZones = useMemo(
    () => zones.map((z) => ({
      ...z,
      brackets: [...(z.brackets || [])]
        .filter((b) => Number(b.min_weight || 0) <= MAX_ONLINE_ORDER_WEIGHT_G)
        .sort((a, b) => a.min_weight - b.min_weight),
    })),
    [zones]
  );

  return (
    <div className="mx-auto max-w-[1100px] px-6 py-14" data-testid="shipping-page">
      <div className="mono text-[11px] uppercase tracking-[0.25em] text-primary">{t("ship_eyebrow")}</div>
      <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter sm:text-5xl">{t("ship_title")}</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground"><C k="ship_intro" /></p>

      {/* Highlights */}
      <div className="mt-10 grid grid-cols-1 gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
        {[Truck, Scale, Clock, PackageCheck].map((Icon, i) => (
          <div key={i} className="bg-card p-6">
            <Icon className="h-6 w-6 text-primary" strokeWidth={1.8} />
            <div className="mt-3 font-heading text-lg font-semibold">{t(`ship_hl${i + 1}_title`)}</div>
            <p className="mt-1 text-sm text-muted-foreground"><C k={`ship_hl${i + 1}_body`} /></p>
          </div>
        ))}
      </div>

      {/* Zone tables */}
      <h2 className="mt-14 font-heading text-2xl font-bold">{t("ship_rates_title")}</h2>
      <p className="mt-2 text-sm text-muted-foreground"><C k="ship_rates_intro" /></p>

      <div className="mt-6 space-y-8">
        {sortedZones.map((z) => (
          <div key={z.name} className="border border-border" data-testid={`zone-${z.tier}`}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-card px-5 py-4">
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Tier {z.tier}</div>
                <div className="font-heading text-xl font-semibold">{z.name}</div>
              </div>
              {z.free_shipping_threshold != null && (
                <span className="mono border border-primary bg-primary/15 px-3 py-1 text-[11px] uppercase tracking-[0.12em] text-primary">Free over {gbp(z.free_shipping_threshold)}</span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-px bg-border sm:grid-cols-3 lg:grid-cols-5">
              {(z.brackets || []).map((b) => (
                <div key={b.label} className="bg-background p-4 text-center">
                  <div className="mono text-[10px] uppercase tracking-[0.1em] text-muted-foreground">{b.label}</div>
                  <div className="mono mt-1 text-lg font-bold">{gbp(b.price)}</div>
                </div>
              ))}
            </div>
            {z.countries?.length ? (
              <div className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
                <span className="mono uppercase tracking-[0.15em] text-foreground">Countries: </span>{z.countries.join(", ")}
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {/* Policies */}
      <h2 className="mt-14 font-heading text-2xl font-bold">{t("ship_policies_title")}</h2>
      <div className="mt-4 space-y-5 text-sm leading-relaxed text-muted-foreground">
        {[Clock, Scale, ShieldCheck, PackageCheck].map((Icon, i) => (
          <div key={i}>
            <div className="flex items-center gap-2 font-heading text-base font-semibold text-foreground"><Icon className="h-4 w-4 text-primary" /> {t(`ship_pol${i + 1}_title`)}</div>
            <p className="mt-1"><C k={`ship_pol${i + 1}_body`} />{i === 1 && <> <span data-testid="shipping-heavy-order-contact-link"><C k={isPartner ? "ship_pol2_heavy_partner" : "ship_pol2_heavy"} /></span></>}</p>
          </div>
        ))}
      </div>

      {fingerprint && <>
      <h2 className="mt-14 font-heading text-2xl font-bold">{t(isPartner ? "ship_pgp_title_partner" : "ship_pgp_title")}</h2>
      <div className="mt-4 border border-border bg-card p-6" data-testid="pgp-fingerprint-section">
        <div className="flex items-center gap-2 font-heading text-base font-semibold text-foreground">
          <KeyRound className="h-4 w-4 text-primary" /> {t("ship_pgp_label")}
        </div>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground"><C k={isPartner ? "ship_pgp_body_partner" : "ship_pgp_body"} /></p>
        <div
          data-testid="pgp-fingerprint"
          className="mt-4 select-all break-words border border-border bg-background px-4 py-3 font-mono text-sm font-semibold tracking-wide text-primary"
        >
          {fingerprint}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">Key ID: {isPartner ? (partner?.pgp_uid || brand.name) : "DreamLabsUK <info@dreamlabs.uk>"}</p>
      </div>
      </>}

      <div className="mt-12 flex flex-wrap items-center gap-3 border border-border bg-card p-6">
        <div className="flex-1">
          <div className="font-heading text-lg font-semibold">{t("ship_cta_title")}</div>
          <p className="text-sm text-muted-foreground"><C k="ship_cta_body" /></p>
        </div>
        <Link to="/build-your-box" data-testid="shipping-shop-cta" className="bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform hover:-translate-y-0.5">{cats.build.title}</Link>
      </div>
    </div>
  );
}
